interface IMimeType {
  extension: string;
  mimeType: string;
  contentType: IContentTypeEnum;
}

export enum IContentTypeEnum {
  Data = "Data",
  Document = "Document",
  Spreadsheet = "Spreadsheet",
  Presentation = "Presentation",
  Image = "Image",
  Video = "Video",
  Audio = "Audio",
  ImageDocument = "ImageDocument",
}

export const mimeTypes: Record<IContentTypeEnum, Record<string, IMimeType>> = {
  ImageDocument: {
    jpeg: {
      extension: ".jpeg",
      contentType: IContentTypeEnum.Image,
      mimeType: "image/jpeg",
    },
    png: {
      extension: ".png",
      contentType: IContentTypeEnum.Image,
      mimeType: "image/png",
    },
    gif: {
      extension: ".gif",
      contentType: IContentTypeEnum.Image,
      mimeType: "image/gif",
    },
    svg: {
      extension: ".svg",
      contentType: IContentTypeEnum.Image,
      mimeType: "image/svg+xml",
    },
    pdf: {
      extension: ".pdf",
      contentType: IContentTypeEnum.Document,
      mimeType: "application/pdf",
    },
    doc: {
      extension: ".doc",
      contentType: IContentTypeEnum.Document,
      mimeType: "application/msword",
    },
    docx: {
      extension: ".docx",
      contentType: IContentTypeEnum.Document,
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    },
  },
  Image: {
    jpeg: {
      extension: ".jpeg",
      contentType: IContentTypeEnum.Image,
      mimeType: "image/jpeg",
    },
    png: {
      extension: ".png",
      contentType: IContentTypeEnum.Image,
      mimeType: "image/png",
    },
    gif: {
      extension: ".gif",
      contentType: IContentTypeEnum.Image,
      mimeType: "image/gif",
    },
    svg: {
      extension: ".svg",
      contentType: IContentTypeEnum.Image,
      mimeType: "image/svg+xml",
    },
  },
  Audio: {
    mp3: {
      extension: ".mp3",
      contentType: IContentTypeEnum.Audio,
      mimeType: "audio/mpeg",
    },
    ogg: {
      extension: ".ogg",
      contentType: IContentTypeEnum.Audio,
      mimeType: "audio/ogg",
    },
  },
  Video: {
    mp4: {
      extension: ".mp4",
      contentType: IContentTypeEnum.Video,
      mimeType: "video/mp4",
    },
    webm: {
      extension: ".webm",
      contentType: IContentTypeEnum.Video,
      mimeType: "video/webm",
    },
    threegp: {
      extension: ".3gp",
      contentType: IContentTypeEnum.Video,
      mimeType: "video/3gpp",
    },
  },
  Document: {
    pdf: {
      extension: ".pdf",
      contentType: IContentTypeEnum.Document,
      mimeType: "application/pdf",
    },
    doc: {
      extension: ".doc",
      contentType: IContentTypeEnum.Document,
      mimeType: "application/msword",
    },
    docx: {
      extension: ".docx",
      contentType: IContentTypeEnum.Document,
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    },
  },
  Spreadsheet: {
    xls: {
      extension: ".xls",
      contentType: IContentTypeEnum.Spreadsheet,
      mimeType: "application/vnd.ms-excel",
    },
    xlsx: {
      extension: ".xlsx",
      contentType: IContentTypeEnum.Spreadsheet,
      mimeType:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
    csv: {
      extension: ".csv",
      contentType: IContentTypeEnum.Spreadsheet,
      mimeType: "text/csv",
    },
  },
  Presentation: {
    ppt: {
      extension: ".ppt",
      contentType: IContentTypeEnum.Presentation,
      mimeType: "application/vnd.ms-powerpoint",
    },
    pptx: {
      extension: ".pptx",
      contentType: IContentTypeEnum.Presentation,
      mimeType:
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    },
  },
  Data: {
    json: {
      extension: ".json",
      contentType: IContentTypeEnum.Data,
      mimeType: "application/json",
    },
    xml: {
      extension: ".xml",
      contentType: IContentTypeEnum.Data,
      mimeType: "application/xml",
    },
  },
} as const;

const flattenMimeTypes = (): IMimeType[] => {
  return Object.values(mimeTypes).flatMap((contentTypeObject) =>
    Object.values(contentTypeObject),
  );
};

const flattenedMimeTypes = flattenMimeTypes();

// Utility to get MIME type by extension
export const getMimeTypeByExtension = (
  extension: string,
): IMimeType | undefined => {
  return flattenedMimeTypes.find((mime) => mime.extension === extension);
};

// Utility to get MIME type by name
export const getMimeType = (mimeType: string): IMimeType | undefined => {
  return flattenedMimeTypes.find(
    (mime) => mime.mimeType === mimeType.trim().toLowerCase(),
  );
};

// Utility to get MIME type by content type
export const getMimeTypeByContentType = (
  contentType: IContentTypeEnum,
): string[] => {
  return flattenedMimeTypes
    .filter((mime) => mime.contentType === contentType)
    .map((mime) => mime.mimeType);
};

// Utility to get MIME type by mime type name or extension
export const lookUpMimeType = (content: string): IMimeType | undefined => {
  const search = content.trim().toLowerCase();

  if (search === "") {
    return undefined;
  }
  return flattenedMimeTypes.find(
    (mime) =>
      mime.extension === toExtension(search) || mime.mimeType === search,
  );
};

export const getMimeTypesExtensions = (mimeTypes: IMimeType[]) => {
  return mimeTypes.map((item) => item.extension);
};

export const getExtensionsOfContentType = (
  contentType: IContentTypeEnum,
): string[] => {
  return getMimeTypesExtensions(
    flattenedMimeTypes.filter((item) => item.contentType === contentType),
  );
};

export const toExtension = (extension: string) => {
  if (extension.trim() === "") {
    return "";
  }
  return `.${extension.trim().toLowerCase().replace(".", "")}`;
};

export const uppercaseExtension = (extension: string): string => {
  return extension.trim().toUpperCase().replace(".", "");
};
